import React, { useState, useEffect } from "react";
import { LogIn, UserPlus, Loader2, Eye, EyeOff, KeyRound, ArrowLeft, Mail, CheckCircle } from "lucide-react";

const API = "/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const xhrPost = (url, data) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.onload = () => {
      let json = null;
      try { json = JSON.parse(xhr.responseText); } catch {}
      resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data: json });
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(JSON.stringify(data));
  });
};

const xhrGet = (url) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    const token = localStorage.getItem("access_token");
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.onload = () => {
      let json = null;
      try { json = JSON.parse(xhr.responseText); } catch {}
      resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data: json });
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send();
  });
};

export default function AuthModal({ onLogin }) {
  // "login" | "register" | "forgot" | "reset"
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");

  // Check URL for reset_token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    if (token) {
      setResetToken(token);
      setMode("reset");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleLoginRegister = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);

    const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
    const body = mode === "login" ? { email, password } : { email, username, password };

    try {
      const res = await xhrPost(`${API}${endpoint}`, body);
      if (!res.ok) {
        const msgs = { 401: "Invalid email or password", 409: "Email already registered", 400: "Please check your input and try again", 429: "Too many attempts. Try again in 15 minutes." };
        let errorMsg = msgs[res.status] || `Request failed (${res.status})`;
        if (res.data?.detail) errorMsg = formatApiErrorDetail(res.data.detail);
        throw new Error(errorMsg);
      }
      let user = res.data;
      // Store tokens in localStorage for reliable auth
      if (user.access_token) localStorage.setItem("access_token", user.access_token);
      if (user.refresh_token) localStorage.setItem("refresh_token", user.refresh_token);
      if (!user || !user.id) {
        const meRes = await xhrGet(`${API}/auth/me`);
        if (meRes.ok && meRes.data) user = meRes.data;
        else throw new Error("Login succeeded but failed to get user data");
      }
      onLogin(user);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      const res = await xhrPost(`${API}/auth/forgot-password`, { email });
      if (res.ok) {
        setSuccess("If an account with that email exists, a password reset link has been sent. Check your inbox.");
      } else {
        throw new Error((res.data?.detail) || "Failed to send reset email");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally { setLoading(false); }
  };

  const handleForgotUsername = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      const res = await xhrPost(`${API}/auth/forgot-username`, { email });
      if (res.ok) {
        setSuccess("If an account with that email exists, we've emailed your username. Check your inbox.");
      } else {
        throw new Error((res.data?.detail) || "Failed to send username email");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      const res = await xhrPost(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      if (res.ok) {
        setSuccess("Password reset successfully! You can now log in.");
        setTimeout(() => { setMode("login"); setSuccess(""); setResetToken(""); }, 2500);
      } else {
        const msgs = { 400: (res.data?.detail) || "Invalid or expired reset link" };
        throw new Error(msgs[res.status] || (res.data?.detail) || "Failed to reset password");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally { setLoading(false); }
  };

  const switchMode = (newMode) => { setMode(newMode); setError(""); setSuccess(""); };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} />

      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black tracking-tight text-white">
            BingoGrid <span className="text-rose-500">AI</span>
          </h1>
        </div>

        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-8 backdrop-blur-xl shadow-2xl">

          {/* ─── Forgot Password Mode ─── */}
          {mode === "forgot" && (
            <>
              <button type="button" onClick={() => switchMode("login")}
                className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-xs font-medium mb-4">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Log In
              </button>
              <h2 className="text-lg font-bold text-white mb-1">Forgot Password?</h2>
              <p className="text-zinc-500 text-xs mb-5">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-400 mb-1.5 uppercase tracking-wider">Email</label>
                  <input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 transition-colors text-sm" />
                </div>
                {error && <div data-testid="auth-error" className="text-rose-400 text-sm font-medium bg-rose-500/10 border border-rose-500/20 rounded-lg px-4 py-2.5">{error}</div>}
                {success && <div data-testid="auth-success" className="text-emerald-400 text-sm font-medium bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-2.5 flex items-center gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" />{success}</div>}
                <button data-testid="forgot-submit-btn" type="submit" disabled={loading}
                  className="w-full py-3 bg-white text-black font-bold rounded-lg hover:bg-zinc-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" />Send Reset Link</>}
                </button>
              </form>
              <p className="text-center mt-4">
                <button data-testid="forgot-username-link" type="button" onClick={() => switchMode("forgot-username")}
                  className="text-zinc-500 hover:text-rose-400 transition-colors text-xs font-medium">
                  Forgot your username?
                </button>
              </p>
            </>
          )}

          {/* ─── Forgot Username Mode ─── */}
          {mode === "forgot-username" && (
            <>
              <button type="button" onClick={() => switchMode("login")}
                className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-xs font-medium mb-4">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Log In
              </button>
              <h2 className="text-lg font-bold text-white mb-1">Forgot Username?</h2>
              <p className="text-zinc-500 text-xs mb-5">Enter your email and we'll send you a reminder.</p>
              <form onSubmit={handleForgotUsername} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-400 mb-1.5 uppercase tracking-wider">Email</label>
                  <input data-testid="forgot-username-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 transition-colors text-sm" />
                </div>
                {error && <div data-testid="auth-error" className="text-rose-400 text-sm font-medium bg-rose-500/10 border border-rose-500/20 rounded-lg px-4 py-2.5">{error}</div>}
                {success && <div data-testid="auth-success" className="text-emerald-400 text-sm font-medium bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-2.5 flex items-center gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" />{success}</div>}
                <button data-testid="forgot-username-submit-btn" type="submit" disabled={loading}
                  className="w-full py-3 bg-white text-black font-bold rounded-lg hover:bg-zinc-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" />Email My Username</>}
                </button>
              </form>
            </>
          )}

          {/* ─── Reset Password Mode ─── */}
          {mode === "reset" && (
            <>
              <h2 className="text-lg font-bold text-white mb-1">Reset Password</h2>
              <p className="text-zinc-500 text-xs mb-5">Enter your new password below.</p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-400 mb-1.5 uppercase tracking-wider">New Password</label>
                  <div className="relative">
                    <input data-testid="reset-password-input" type={showPassword ? "text" : "password"} value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)} required minLength={6} placeholder="Min 6 characters"
                      className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 transition-colors text-sm pr-12" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {error && <div data-testid="auth-error" className="text-rose-400 text-sm font-medium bg-rose-500/10 border border-rose-500/20 rounded-lg px-4 py-2.5">{error}</div>}
                {success && <div data-testid="auth-success" className="text-emerald-400 text-sm font-medium bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-2.5 flex items-center gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" />{success}</div>}
                <button data-testid="reset-submit-btn" type="submit" disabled={loading}
                  className="w-full py-3 bg-white text-black font-bold rounded-lg hover:bg-zinc-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4" />Reset Password</>}
                </button>
              </form>
              <p className="text-center text-zinc-600 text-xs mt-4">
                <button type="button" onClick={() => switchMode("login")} className="text-zinc-400 hover:text-white transition-colors font-medium">Back to Log In</button>
              </p>
            </>
          )}

          {/* ─── Login / Register Mode ─── */}
          {(mode === "login" || mode === "register") && (
            <>
              <div className="flex bg-zinc-800/60 rounded-lg p-1 mb-6">
                <button data-testid="auth-tab-login" type="button" onClick={() => switchMode("login")}
                  className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${mode === "login" ? "bg-white text-black shadow-sm" : "text-zinc-400 hover:text-zinc-200"}`}>
                  Log In
                </button>
                <button data-testid="auth-tab-register" type="button" onClick={() => switchMode("register")}
                  className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${mode === "register" ? "bg-white text-black shadow-sm" : "text-zinc-400 hover:text-zinc-200"}`}>
                  Sign Up
                </button>
              </div>

              <form onSubmit={handleLoginRegister} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-400 mb-1.5 uppercase tracking-wider">Email</label>
                  <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 transition-colors text-sm" />
                </div>

                {mode === "register" && (
                  <div>
                    <label className="block text-xs font-bold text-zinc-400 mb-1.5 uppercase tracking-wider">Username</label>
                    <input data-testid="auth-username-input" type="text" value={username} onChange={(e) => setUsername(e.target.value)} required
                      placeholder="Your display name"
                      className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 transition-colors text-sm" />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold text-zinc-400 mb-1.5 uppercase tracking-wider">Password</label>
                  <div className="relative">
                    <input data-testid="auth-password-input" type={showPassword ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)} required minLength={6}
                      placeholder={mode === "register" ? "Min 6 characters" : "Your password"}
                      className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 transition-colors text-sm pr-12" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && <div data-testid="auth-error" className="text-rose-400 text-sm font-medium bg-rose-500/10 border border-rose-500/20 rounded-lg px-4 py-2.5">{error}</div>}

                <button data-testid="auth-submit-btn" type="submit" disabled={loading}
                  className="w-full py-3 bg-white text-black font-bold rounded-lg hover:bg-zinc-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? <><LogIn className="w-4 h-4" />Log In</> : <><UserPlus className="w-4 h-4" />Create Account</>}
                </button>
              </form>

              {mode === "login" && (
                <p className="text-center mt-4">
                  <button data-testid="forgot-password-link" type="button" onClick={() => switchMode("forgot")}
                    className="text-zinc-500 hover:text-rose-400 transition-colors text-xs font-medium">
                    Forgot password?
                  </button>
                </p>
              )}
            </>
          )}
        </div>

        {(mode === "login" || mode === "register") && (
          <p className="text-center text-zinc-600 text-xs mt-6">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button type="button" onClick={() => switchMode(mode === "login" ? "register" : "login")}
              className="text-zinc-400 hover:text-white transition-colors font-medium">
              {mode === "login" ? "Sign up" : "Log in"}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
